import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaPlus, FaPrint, FaEdit, FaPaperPlane, FaCheck, FaCheckCircle, FaCodeBranch, FaGraduationCap, FaSyncAlt, FaExclamationTriangle, FaEye, FaArchive } from 'react-icons/fa';
import { cvDocumentsAPI, samplingPlansAPI, commercialConfigAPI } from '../services/api';
import { Modal, ModalFooter } from './Modals';
import ApprovalPanel from './ApprovalPanel';
import { printAssessmentReport } from '../utils/assessmentReports';
import { cvDocumentHTML, cvReportKey, STUDY_LABELS } from '../utils/cvDocumentReport';
import { useAppSettings } from '../context/AppSettingsContext';

// Documents → CV Protocols & Reports. Fixed-template protocol and report for each
// sampling plan (CV / CCV / CEHT / DEHT / Revalidation): prepare → e-sign submit
// (content frozen) → Approval Flow levels → make effective (protocol → training course
// + AiventraLMS) → report after execution. Approved / effective documents are checked
// against current master data and flagged when a revision is needed.

const FLOW = 'cv_document.approval';
const PENDING = ['Pending Approval', 'Pending QA Approval'];
const STATUS_TONE = { Draft: 'secondary', 'Pending Approval': 'warning', 'Pending QA Approval': 'warning', Approved: 'info', Effective: 'success', Superseded: 'secondary', Rejected: 'danger' };
const EMPTY = { plan_id: '', doc_type: 'protocol', study_type: '', title: '', purpose: '', scope: '', responsibilities: '', additional_notes: '', revision_reason: '' };
const getRows = (res) => (Array.isArray(res?.data?.data) ? res.data.data : []);

const CvDocumentsPanel = () => {
  const { appName } = useAppSettings();
  const [docs, setDocs] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');
  const [form, setForm] = useState(null); // create / edit
  const [detail, setDetail] = useState(null); // { document, content, approval_trail, training, changes }
  const [sign, setSign] = useState(null); // { title, decisionOptions, run }
  const [signForm, setSignForm] = useState({ password: '', comment: '', decision: 'approve' });
  const [revise, setRevise] = useState(null); // { doc, reason }
  const [busy, setBusy] = useState(false);
  const [dmsActive, setDmsActive] = useState(false);

  const role = useMemo(() => { try { return String(JSON.parse(localStorage.getItem('user') || '{}').role || '').toLowerCase(); } catch { return ''; } }, []);
  const canMakeEffective = ['qa', 'qa head', 'admin', 'super admin'].includes(role);

  const load = async () => {
    try {
      const [docRes, planRes] = await Promise.all([cvDocumentsAPI.list(), samplingPlansAPI.getAll().catch(() => null)]);
      setDocs(getRows(docRes));
      const planRows = Array.isArray(planRes?.data?.data?.rows) ? planRes.data.data.rows : getRows(planRes);
      setPlans(planRows);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to load CV protocols & reports');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    commercialConfigAPI.getRuntime()
      .then((res) => setDmsActive(!!res?.data?.data?.integrations?.aiventra_dms?.features?.document_push))
      .catch(() => {});
  }, []);

  const openDetail = async (doc) => {
    try {
      const res = await cvDocumentsAPI.get(doc.doc_id);
      setDetail(res?.data?.data || null);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to open document');
    }
  };

  const refreshDetail = async () => {
    await load();
    if (detail?.document?.doc_id) await openDetail(detail.document);
  };

  const print = (doc) => printAssessmentReport({
    title: doc.title,
    documentName: doc.title,
    appName,
    reportKey: cvReportKey(doc.doc_type, doc.study_type),
    build: async () => cvDocumentHTML((await cvDocumentsAPI.get(doc.doc_id))?.data?.data)
  });

  const requestSign = (config) => { setSignForm({ password: '', comment: '', decision: 'approve' }); setSign(config); };
  const runSign = async () => {
    if (!signForm.password) return toast.warning('Enter your password');
    if (!signForm.comment.trim()) return toast.warning('Enter the reason / comment');
    setBusy(true);
    try {
      const res = await sign.run({ password: signForm.password, comment: signForm.comment, decision: signForm.decision });
      toast.success(res?.data?.message || 'Done', { autoClose: 8000 });
      if (res?.data?.lms?.status === 'failed') toast.warning(`LMS hand-off failed: ${res.data.lms.error}`, { autoClose: false });
      setSign(null);
      await refreshDetail();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const saveForm = async () => {
    if (!form.doc_id && !form.plan_id) return toast.warning('Select a sampling plan');
    setBusy(true);
    try {
      if (form.doc_id) {
        await cvDocumentsAPI.update(form.doc_id, form);
        toast.success('Draft saved');
      } else {
        const res = await cvDocumentsAPI.create(form);
        toast.success(res?.data?.message || 'Document created');
      }
      setForm(null);
      await refreshDetail();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save');
    } finally {
      setBusy(false);
    }
  };

  const doRevise = async () => {
    if (!revise.reason.trim()) return toast.warning('Give the reason for revision');
    setBusy(true);
    try {
      const res = await cvDocumentsAPI.revise(revise.doc.doc_id, { revision_reason: revise.reason });
      toast.success(res?.data?.message || 'Revision created');
      setRevise(null);
      setDetail(null);
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to revise');
    } finally {
      setBusy(false);
    }
  };

  const lmsAction = async (fn, doc) => {
    setBusy(true);
    try {
      const res = await fn(doc.doc_id);
      toast.success(res?.data?.message || 'Done', { autoClose: 9000 });
      await refreshDetail();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const filtered = docs.filter((d) => filter === 'All' || (filter === 'Revision required' ? d.revision_required : d.status === filter || d.doc_type === filter));
  const revisionCount = docs.filter((d) => d.revision_required).length;
  const selectedPlan = plans.find((p) => String(p.plan_id) === String(form?.plan_id));

  const actionsFor = (d, inDetail = false) => (
    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
      {!inDetail && <button className="btn btn-sm btn-outline" onClick={() => openDetail(d)} title="Open"><FaEye /></button>}
      <button className="btn btn-sm btn-outline" onClick={() => print(d)} title="Print / save as PDF"><FaPrint />{inDetail && ' Print / PDF'}</button>
      {d.status === 'Draft' && (
        <>
          <button className="btn btn-sm btn-outline" onClick={() => setForm({ ...EMPTY, ...d })} title="Edit text"><FaEdit />{inDetail && ' Edit'}</button>
          <button className="btn btn-sm btn-primary" title="Submit for approval (freezes the data)" onClick={() => requestSign({
            title: `Submit ${d.document_number} v${d.version} for approval`,
            note: 'The current master data is frozen into this document; reviewers approve exactly what prints.',
            run: (s) => cvDocumentsAPI.submit(d.doc_id, s)
          })}><FaPaperPlane />{inDetail && ' Submit'}</button>
        </>
      )}
      {d.status === 'Approved' && canMakeEffective && (
        <button className="btn btn-sm btn-success" title="Make effective" onClick={() => requestSign({
          title: `Make ${d.document_number} v${d.version} effective`,
          note: d.doc_type === 'protocol' ? 'Supersedes the previous effective version, creates the mandatory training course and sends it to AiventraLMS (when LMS training sync is on).' : 'Supersedes the previous effective version.',
          run: (s) => cvDocumentsAPI.makeEffective(d.doc_id, s)
        })}><FaCheckCircle />{inDetail && ' Make Effective'}</button>
      )}
      {['Approved', 'Effective'].includes(d.status) && (
        <button className={`btn btn-sm ${d.revision_required ? 'btn-warning' : 'btn-outline'}`} title="Prepare the next version"
          onClick={() => setRevise({ doc: d, reason: (d.changes || []).map((c) => `${c.item}: ${c.was} → ${c.now}`).join('\n') })}>
          <FaCodeBranch />{inDetail && ' Revise'}
        </button>
      )}
      {d.status === 'Effective' && d.doc_type === 'protocol' && (
        <>
          <button className="btn btn-sm btn-outline" disabled={busy} onClick={() => lmsAction(cvDocumentsAPI.sendToLms, d)} title="Send / re-send to AiventraLMS for training"><FaGraduationCap />{inDetail && ' Send to LMS'}</button>
          <button className="btn btn-sm btn-outline" disabled={busy} onClick={() => lmsAction(cvDocumentsAPI.pullTraining, d)} title="Pull training completions from AiventraLMS"><FaSyncAlt />{inDetail && ' Pull Training'}</button>
        </>
      )}
      {d.status === 'Effective' && dmsActive && (
        <button className="btn btn-sm btn-outline" disabled={busy} onClick={() => lmsAction(cvDocumentsAPI.sendToDms, d)} title="Send / re-send to the DMS"><FaArchive />{inDetail && ' Send to DMS'}</button>
      )}
    </div>
  );

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;

  return (
    <div className="card" style={{ marginBottom: '18px' }}>
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
        <div>
          <strong>CV Protocols &amp; Reports</strong>
          <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>Fixed-template protocol and report per sampling plan — CV, CCV, CEHT, DEHT and Revalidation.</div>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <select className="form-control" style={{ width: 'auto' }} value={filter} onChange={(e) => setFilter(e.target.value)}>
            {['All', 'protocol', 'report', 'Draft', 'Pending Approval', 'Pending QA Approval', 'Approved', 'Effective', 'Superseded', 'Revision required'].map((f) => (
              <option key={f} value={f}>{f === 'protocol' ? 'Protocols' : f === 'report' ? 'Reports' : f}</option>
            ))}
          </select>
          <button className="btn btn-primary" onClick={() => setForm({ ...EMPTY })}><FaPlus /> New Protocol / Report</button>
        </div>
      </div>
      {revisionCount > 0 && (
        <div style={{ margin: '10px 14px 0', padding: '8px 12px', borderRadius: '8px', background: 'var(--warning-bg, #fffbeb)', color: 'var(--warning-text, #92400e)', display: 'flex', gap: '8px', alignItems: 'center' }}>
          <FaExclamationTriangle /> {revisionCount} approved / effective document(s) no longer match current master data — a revised protocol / report is required.
        </div>
      )}
      <div className="table-container">
        <table>
          <thead>
            <tr><th>Document No.</th><th>Ver.</th><th>Type</th><th>Study</th><th>Plan / Train</th><th>Status</th><th>LMS</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={8} className="text-center text-muted">No protocols or reports yet. Use "New Protocol / Report".</td></tr>
            ) : filtered.map((d) => (
              <tr key={d.doc_id}>
                <td>
                  <span className="record-link" onClick={() => openDetail(d)} title="Open this document">{d.document_number}</span>
                  <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)' }}>{d.title}</div>
                </td>
                <td>{String(d.version).padStart(2, '0')}</td>
                <td style={{ textTransform: 'capitalize' }}>{d.doc_type}</td>
                <td>{d.study_type}</td>
                <td>
                  {d.plan_id ? (
                    <Link to={`/study-report/${d.plan_id}`} className="record-link" title="View sampling plan / study report">{d.plan_code}</Link>
                  ) : d.plan_code}
                  {d.train_id ? (
                    <div style={{ fontSize: 'var(--fs-xs)' }}>
                      <Link to="/equipment-trains" state={{ openViewTrainId: d.train_id }} className="record-link" title="View train">{d.train_code}</Link>
                    </div>
                  ) : d.train_code ? <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)' }}>{d.train_code}</div> : null}
                </td>
                <td>
                  <span className={`badge badge-${STATUS_TONE[d.status] || 'secondary'}`}>{d.status}</span>
                  {d.revision_required && (
                    <div title={(d.changes || []).map((c) => `${c.item}: ${c.was} → ${c.now}`).join('\n')} style={{ fontSize: 'var(--fs-xs)', color: 'var(--danger-color, #dc2626)', marginTop: '3px', cursor: 'help' }}>
                      <FaExclamationTriangle /> Revision required ({d.changes.length})
                    </div>
                  )}
                </td>
                <td style={{ fontSize: 'var(--fs-sm)' }}>
                  {d.doc_type === 'protocol' ? (d.lms_status || '—') : '—'}
                  {dmsActive && d.status === 'Effective' && <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)' }} title={d.dms_error || ''}>DMS: {d.dms_status || 'not sent'}</div>}
                </td>
                <td>{actionsFor(d)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create / edit */}
      <Modal isOpen={!!form} onClose={() => setForm(null)} title={form?.doc_id ? `Edit ${form.document_number} v${form.version}` : 'New CV Protocol / Report'} size="lg">
        {form && (
          <>
            <div className="form-grid">
              {!form.doc_id && (
                <>
                  <div className="form-group">
                    <label className="form-label">Sampling Plan *</label>
                    <select className="form-control" value={form.plan_id} onChange={(e) => setForm((p) => ({ ...p, plan_id: e.target.value }))}>
                      <option value="">Select…</option>
                      {plans.map((p) => <option key={p.plan_id} value={p.plan_id}>{p.plan_code} — {p.study_type}{p.train_name ? ` (${p.train_name})` : ''} [{p.status}]</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Document</label>
                    <select className="form-control" value={form.doc_type} onChange={(e) => setForm((p) => ({ ...p, doc_type: e.target.value }))}>
                      <option value="protocol">Protocol</option>
                      <option value="report">Report</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Study type</label>
                    <select className="form-control" value={form.study_type} onChange={(e) => setForm((p) => ({ ...p, study_type: e.target.value }))}>
                      <option value="">From plan{selectedPlan ? ` (${selectedPlan.study_type})` : ''}</option>
                      <option value="REVALIDATION">Revalidation</option>
                    </select>
                  </div>
                </>
              )}
              <div className="form-group form-group-wide">
                <label className="form-label">Title</label>
                <input className="form-control" value={form.title || ''} placeholder={selectedPlan ? `${STUDY_LABELS[(form.study_type || selectedPlan.study_type || 'CV').toUpperCase()] || 'Cleaning Validation'} ${form.doc_type === 'report' ? 'Report' : 'Protocol'} — ${selectedPlan.plan_code}` : 'Auto-generated if blank'} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} />
              </div>
              {!form.doc_id && (
                <div className="form-group form-group-wide">
                  <label className="form-label">Reason for new version (only if this document number already exists)</label>
                  <input className="form-control" value={form.revision_reason || ''} onChange={(e) => setForm((p) => ({ ...p, revision_reason: e.target.value }))} />
                </div>
              )}
              {[['purpose', 'Objective / purpose'], ['scope', 'Scope'], ['responsibilities', 'Responsibilities'], ['additional_notes', 'Additional notes / remarks']].map(([field, label]) => (
                <div key={field} className="form-group form-group-wide">
                  <label className="form-label">{label}</label>
                  <textarea className="form-control" rows={3} value={form[field] || ''} placeholder="Leave blank to use the standard template text for this study type" onChange={(e) => setForm((p) => ({ ...p, [field]: e.target.value }))} />
                </div>
              ))}
            </div>
            <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>
              Everything else — equipment and surface areas, products and APIs, worst case, MACO / MAR / swab and rinse limits, cleaning agent and rinsing solvent, analytical methods and recovery,
              VRL, sampling locations, hold-time design and the approvals of each — is filled from the masters automatically. A report needs an approved protocol for the same plan.
            </p>
            <ModalFooter>
              <button className="btn btn-outline" onClick={() => setForm(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveForm} disabled={busy}>{busy ? 'Saving…' : form.doc_id ? 'Save Draft' : 'Create Draft'}</button>
            </ModalFooter>
          </>
        )}
      </Modal>

      {/* Detail */}
      <Modal isOpen={!!detail} onClose={() => setDetail(null)} title={detail ? `${detail.document.document_number} v${String(detail.document.version).padStart(2, '0')} — ${detail.document.status}` : ''} size="lg">
        {detail && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div><strong>{detail.document.title}</strong>{detail.document.revision_reason && <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>Revision reason: {detail.document.revision_reason}</div>}</div>
            {actionsFor({ ...detail.document, changes: detail.changes, revision_required: (detail.changes || []).length > 0 }, true)}
            {(detail.changes || []).length > 0 && (
              <div style={{ border: '1px solid var(--warning-color, #d97706)', borderRadius: '8px', padding: '10px' }}>
                <strong><FaExclamationTriangle /> Revision required — master data changed since approval</strong>
                <table style={{ marginTop: '6px', width: '100%', fontSize: 'var(--fs-sm)' }}>
                  <thead><tr><th>Item</th><th>In document</th><th>Now</th></tr></thead>
                  <tbody>{detail.changes.map((c) => <tr key={c.item}><td>{c.item}</td><td>{String(c.was)}</td><td>{String(c.now)}</td></tr>)}</tbody>
                </table>
              </div>
            )}
            <ApprovalPanel workflowType={FLOW} entityId={detail.document.doc_id} pending={PENDING.includes(detail.document.status)} refreshKey={detail.document.status}
              onSign={() => requestSign({
                title: `Approval decision — ${detail.document.document_number} v${detail.document.version}`,
                decisionOptions: true,
                run: (s) => cvDocumentsAPI.decide(detail.document.doc_id, s)
              })} />
            {detail.document.doc_type === 'protocol' && detail.document.status === 'Effective' && (
              <div>
                <strong><FaGraduationCap /> Training on this protocol</strong>
                <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', marginLeft: '8px' }}>LMS: {detail.document.lms_status || 'not sent'}{detail.document.lms_error ? ` — ${detail.document.lms_error}` : ''}</span>
                {Array.isArray(detail.training) && detail.training.length ? (
                  <table style={{ width: '100%', fontSize: 'var(--fs-sm)', marginTop: '6px' }}>
                    <thead><tr><th>User</th><th>Emp. ID</th><th>Date</th><th>Source</th><th>Status</th><th>LMS</th></tr></thead>
                    <tbody>{detail.training.map((r) => <tr key={r.record_id}><td>{r.username}</td><td>{r.employee_id || '—'}</td><td>{r.training_date ? new Date(r.training_date).toLocaleDateString() : '—'}</td><td>{r.source}</td><td>{r.status}</td><td>{r.lms_sync_status}</td></tr>)}</tbody>
                  </table>
                ) : <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', margin: '4px 0 0' }}>No training recorded yet. Completions arrive from AiventraLMS automatically, via "Pull Training", or are logged manually in Training (pushed to the LMS after Head QA approval).</p>}
              </div>
            )}
            <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>
              Contents: {detail.content?.equipment?.length || 0} equipment · {detail.content?.products?.length || 0} products · {detail.content?.locations?.length || 0} sampling locations · {detail.content?.maco?.length || 0} MACO calculations
              {detail.document.doc_type === 'report' && ` · ${detail.content?.results?.length || 0} results — ${detail.content?.summary?.conclusion || ''}`}
            </div>
          </div>
        )}
      </Modal>

      {/* E-signature */}
      <Modal isOpen={!!sign} onClose={() => setSign(null)} title={sign?.title || 'Electronic signature'} size="md">
        {sign && (
          <>
            {sign.note && <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', marginTop: 0 }}>{sign.note}</p>}
            {sign.decisionOptions && (
              <div className="form-group">
                <label className="form-label">Decision</label>
                <select className="form-control" value={signForm.decision} onChange={(e) => setSignForm((p) => ({ ...p, decision: e.target.value }))}>
                  <option value="approve">Approve</option>
                  <option value="return">Return to initiator</option>
                  <option value="reject">Reject</option>
                </select>
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Password *</label>
              <input className="form-control" type="password" autoComplete="current-password" value={signForm.password} onChange={(e) => setSignForm((p) => ({ ...p, password: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Reason / comment *</label>
              <textarea className="form-control" rows={2} value={signForm.comment} onChange={(e) => setSignForm((p) => ({ ...p, comment: e.target.value }))} />
            </div>
            <ModalFooter>
              <button className="btn btn-outline" onClick={() => setSign(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={runSign} disabled={busy}><FaCheck /> {busy ? 'Signing…' : 'Sign'}</button>
            </ModalFooter>
          </>
        )}
      </Modal>

      {/* Revise */}
      <Modal isOpen={!!revise} onClose={() => setRevise(null)} title={revise ? `Revise ${revise.doc.document_number} (v${revise.doc.version} → v${Number(revise.doc.version) + 1})` : ''} size="md">
        {revise && (
          <>
            <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', marginTop: 0 }}>
              Creates the next version as a Draft with today's master data. The current version stays in force until the new one is approved and made effective.
              If the change needs a different study (e.g. a new worst case or new train), create a new protocol on the new sampling plan instead.
            </p>
            <div className="form-group">
              <label className="form-label">Reason for revision *</label>
              <textarea className="form-control" rows={5} value={revise.reason} onChange={(e) => setRevise((p) => ({ ...p, reason: e.target.value }))} />
            </div>
            <ModalFooter>
              <button className="btn btn-outline" onClick={() => setRevise(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={doRevise} disabled={busy}>{busy ? 'Creating…' : 'Create Revision'}</button>
            </ModalFooter>
          </>
        )}
      </Modal>
    </div>
  );
};

export default CvDocumentsPanel;
